import React, { useCallback, useEffect, useRef, useState, type FocusEvent, type PointerEvent } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { DashlyFieldShell } from "../shared/DashlyFieldShell";
import { DashlyControlVariant, dashlyControlVariantClass } from "../shared/dashlyControlVariant";
import "../shared/dashlyFormControl.scss";
import "./DashlyClickableInput.scss";

export const DASHLY_CLICKABLE_INPUT_DEFAULT_BLUR_DELAY_MS = 120;
export const DASHLY_CLICKABLE_INPUT_DEFAULT_OPEN_GRACE_MS = 300;

export type DashlyClickableInputTone = "default" | "subtle" | "accent" | "empty";

export type DashlyClickableInputProps = {
    id?: string;
    /** Committed display value when not editing. While editing, use with `onChange` for the draft. */
    value: string;
    onChange?: (value: string) => void;
    /** Fired when the user confirms with the save button or Enter. */
    onSave?: (value: string) => void | Promise<void>;
    /** Fired when edit mode is dismissed without saving (blur / Escape). */
    onCancel?: () => void;
    /** Fired when the user activates or dismisses edit mode. */
    onEditingChange?: (editing: boolean) => void;

    /** Controlled edit mode — use when only one field may be active (e.g. translation grid). */
    editing?: boolean;
    defaultEditing?: boolean;

    disabled?: boolean;
    /** Never enter edit mode; value is display-only. */
    readOnly?: boolean;
    saving?: boolean;
    saveDisabled?: boolean;
    pending?: boolean;

    placeholder?: string;
    /** Shown in display mode when `value` is empty. */
    emptyPlaceholder?: string;
    editPlaceholder?: string;
    displayTitle?: string;

    saveAriaLabel?: string;
    saveTooltip?: string;
    saveIcon?: string;

    /** Show a remove control (display and edit mode) to clear or revert the value. */
    removable?: boolean;
    removing?: boolean;
    onRemove?: () => void | Promise<void>;
    removeAriaLabel?: string;
    removeTooltip?: string;
    removeIcon?: string;
    /** When set, controls only the remove button (defaults to `disabled || saving || removing`). */
    removeDisabled?: boolean;
    displayGroupClassName?: string;
    removeButtonClassName?: string;

    tone?: DashlyClickableInputTone;
    controlVariant?: DashlyControlVariant;

    className?: string;
    displayClassName?: string;
    editGroupClassName?: string;
    inputClassName?: string;
    saveButtonClassName?: string;

    label?: React.ReactNode;
    labelInfo?: string;
    hint?: React.ReactNode;
    required?: boolean;
    containerClassName?: string;

    blurToCancel?: boolean;
    selectOnEdit?: boolean;
    cancelOnEscape?: boolean;
    commitOnEnter?: boolean;
    openGraceMs?: number;
    blurDelayMs?: number;

    maxLength?: number;
};

const isDashlyClickableInputFocusTarget = (target: EventTarget | null): boolean =>
    target instanceof HTMLElement && target.closest(".dashly-clickable-input") != null;

const DashlyClickableInput = ({
    id,
    value,
    onChange,
    onSave,
    onCancel,
    onEditingChange,
    editing: editingProp,
    defaultEditing = false,
    disabled = false,
    readOnly = false,
    saving = false,
    saveDisabled = false,
    pending = false,
    placeholder,
    emptyPlaceholder,
    editPlaceholder,
    displayTitle,
    saveAriaLabel = "Opslaan",
    saveTooltip = "Opslaan",
    saveIcon = "pi pi-check",
    removable = false,
    removing = false,
    onRemove,
    removeAriaLabel = "Verwijderen",
    removeTooltip = "Verwijderen",
    removeIcon = "pi pi-times",
    removeDisabled,
    displayGroupClassName,
    removeButtonClassName,
    tone = "default",
    controlVariant = "form",
    className,
    displayClassName,
    editGroupClassName,
    inputClassName,
    saveButtonClassName,
    label,
    labelInfo,
    hint,
    required,
    containerClassName,
    blurToCancel = true,
    selectOnEdit = true,
    cancelOnEscape = true,
    commitOnEnter = true,
    openGraceMs = DASHLY_CLICKABLE_INPUT_DEFAULT_OPEN_GRACE_MS,
    blurDelayMs = DASHLY_CLICKABLE_INPUT_DEFAULT_BLUR_DELAY_MS,
    maxLength,
}: DashlyClickableInputProps) => {
    const isControlledEditing = editingProp !== undefined;
    const [internalEditing, setInternalEditing] = useState(defaultEditing);
    const [internalDraft, setInternalDraft] = useState(value);
    const isEditing = isControlledEditing ? editingProp : internalEditing;

    const rootRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const blurTimeoutRef = useRef<number | null>(null);
    const justOpenedRef = useRef(false);

    const interactionDisabled = disabled || readOnly || saving || removing;
    const removeInteractionDisabled = removeDisabled ?? (disabled || saving || removing);
    const showRemove = removable && Boolean(onRemove);
    const draftValue = isControlledEditing || onChange != null ? value : internalDraft;

    const setEditing = useCallback(
        (next: boolean) => {
            if (!isControlledEditing) {
                setInternalEditing(next);
            }
            onEditingChange?.(next);
        },
        [isControlledEditing, onEditingChange],
    );

    const clearPendingBlur = useCallback(() => {
        if (blurTimeoutRef.current != null) {
            window.clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
    }, []);

    const updateDraft = useCallback(
        (next: string) => {
            if (onChange) {
                onChange(next);
                return;
            }
            setInternalDraft(next);
        },
        [onChange],
    );

    const dismissEdit = useCallback(() => {
        clearPendingBlur();
        if (!isControlledEditing) {
            setInternalDraft(value);
        }
        setEditing(false);
        onCancel?.();
    }, [clearPendingBlur, isControlledEditing, onCancel, setEditing, value]);

    const beginEdit = useCallback(() => {
        if (interactionDisabled || isEditing) {
            return;
        }
        if (!isControlledEditing) {
            setInternalDraft(value);
        }
        justOpenedRef.current = true;
        setEditing(true);
    }, [interactionDisabled, isControlledEditing, isEditing, setEditing, value]);

    useEffect(() => {
        if (!isEditing) {
            justOpenedRef.current = false;
            return;
        }

        const frame = window.requestAnimationFrame(() => {
            inputRef.current?.focus();
            if (selectOnEdit) {
                inputRef.current?.select();
            }
        });
        const graceTimer = window.setTimeout(() => {
            justOpenedRef.current = false;
        }, openGraceMs);

        return () => {
            window.cancelAnimationFrame(frame);
            window.clearTimeout(graceTimer);
            clearPendingBlur();
        };
    }, [clearPendingBlur, isEditing, openGraceMs, selectOnEdit]);

    const handleCommit = useCallback(() => {
        if (interactionDisabled || saveDisabled) {
            return;
        }
        clearPendingBlur();
        setEditing(false);
        void onSave?.(draftValue);
    }, [clearPendingBlur, draftValue, interactionDisabled, onSave, saveDisabled, setEditing]);

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
        if (!blurToCancel || justOpenedRef.current) {
            return;
        }
        if (isDashlyClickableInputFocusTarget(event.relatedTarget)) {
            return;
        }

        clearPendingBlur();
        blurTimeoutRef.current = window.setTimeout(() => {
            blurTimeoutRef.current = null;
            if (!rootRef.current?.contains(document.activeElement)) {
                dismissEdit();
            }
        }, blurDelayMs);
    };

    const handleDisplayPointerDown = (event: PointerEvent<HTMLInputElement>) => {
        if (interactionDisabled || event.button !== 0) {
            return;
        }
        beginEdit();
    };

    const handleGroupPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        if (rootRef.current?.contains(event.target as Node)) {
            clearPendingBlur();
        }
    };

    const handleSavePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        handleCommit();
    };

    const handleRemovePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (removeInteractionDisabled) {
            return;
        }
        clearPendingBlur();
        if (isEditing) {
            setEditing(false);
        }
        void onRemove?.();
    };

    const rootClassName = classNames(
        "dashly-clickable-input",
        `dashly-clickable-input--tone-${tone}`,
        controlVariant === "filter" && "dashly-clickable-input--filter",
        isEditing && "dashly-clickable-input--editing",
        pending && "dashly-clickable-input--pending",
        interactionDisabled && "dashly-clickable-input--disabled",
        className,
    );

    const displayShowsEmptyLabel =
        !isEditing && tone === "empty" && !value.trim() && Boolean(emptyPlaceholder?.trim());
    const readonlyDisplayValue = displayShowsEmptyLabel ? emptyPlaceholder!.trim() : value;
    const readonlyPlaceholder = displayShowsEmptyLabel
        ? placeholder
        : value.trim()
          ? placeholder
          : (emptyPlaceholder ?? placeholder);

    const displayFieldClassName = classNames(
        "dashly-clickable-input__display",
        dashlyControlVariantClass("dashly-clickable-input__field", controlVariant),
        "w-full",
        displayShowsEmptyLabel && "dashly-clickable-input__display--empty-label",
        displayClassName,
        inputClassName,
    );

    const editFieldClassName = classNames(
        "dashly-clickable-input__field",
        dashlyControlVariantClass("dashly-clickable-input__field", controlVariant),
        "w-full",
        inputClassName,
    );

    const removeButton = showRemove ? (
        <span className="dashly-clickable-input__remove-wrap">
            <Button
                type="button"
                icon={removeIcon}
                severity="danger"
                text
                rounded
                size="small"
                className={classNames("dashly-clickable-input__remove", removeButtonClassName)}
                disabled={removeInteractionDisabled}
                aria-label={removeAriaLabel}
                tooltip={removeTooltip}
                tooltipOptions={{ position: "top" }}
                onPointerDown={handleRemovePointerDown}
            />
        </span>
    ) : null;

    const control = (
        <div ref={rootRef} className={rootClassName}>
            {!isEditing ? (
                showRemove ? (
                    <div
                        className={classNames(
                            "dashly-clickable-input__display-group",
                            displayGroupClassName,
                        )}
                        onPointerDown={handleGroupPointerDown}
                    >
                        <InputText
                            id={id}
                            readOnly
                            className={displayFieldClassName}
                            value={readonlyDisplayValue}
                            placeholder={readonlyPlaceholder}
                            title={displayTitle}
                            disabled={interactionDisabled}
                            tabIndex={interactionDisabled ? -1 : 0}
                            maxLength={maxLength}
                            onPointerDown={handleDisplayPointerDown}
                        />
                        {removeButton}
                    </div>
                ) : (
                    <InputText
                        id={id}
                        readOnly
                        className={displayFieldClassName}
                        value={readonlyDisplayValue}
                        placeholder={readonlyPlaceholder}
                        title={displayTitle}
                        disabled={interactionDisabled}
                        tabIndex={interactionDisabled ? -1 : 0}
                        maxLength={maxLength}
                        onPointerDown={handleDisplayPointerDown}
                    />
                )
            ) : (
                <div
                    className={classNames("dashly-clickable-input__edit-group", editGroupClassName)}
                    onPointerDown={handleGroupPointerDown}
                >
                    <InputText
                        ref={inputRef}
                        id={id}
                        className={editFieldClassName}
                        value={draftValue}
                        placeholder={editPlaceholder ?? placeholder}
                        disabled={interactionDisabled}
                        maxLength={maxLength}
                        onChange={(event) => updateDraft(event.target.value)}
                        onBlur={handleBlur}
                        onKeyDown={(event) => {
                            if (commitOnEnter && event.key === "Enter") {
                                event.preventDefault();
                                handleCommit();
                            }
                            if (cancelOnEscape && event.key === "Escape") {
                                event.preventDefault();
                                dismissEdit();
                            }
                        }}
                    />
                    {removeButton}
                    <span className="dashly-clickable-input__save-wrap">
                        <Button
                            type="button"
                            icon={saveIcon}
                            severity="success"
                            size="small"
                            className={classNames("dashly-clickable-input__save", saveButtonClassName)}
                            disabled={interactionDisabled || saveDisabled}
                            aria-label={saveAriaLabel}
                            tooltip={saveTooltip}
                            tooltipOptions={{ position: "top" }}
                            onPointerDown={handleSavePointerDown}
                        />
                    </span>
                </div>
            )}
        </div>
    );

    if (!label) {
        return control;
    }

    return (
        <DashlyFieldShell label={label} labelInfo={labelInfo} hint={hint} htmlFor={id} required={required} className={containerClassName}>
            {control}
        </DashlyFieldShell>
    );
};

DashlyClickableInput.displayName = "DashlyClickableInput";

export { DashlyClickableInput };
